import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http'
import { IMessageList } from "../interfaces/types.interface"
import { Observable, BehaviorSubject, map, from, Subscription, Subject, throwError, defer } from "rxjs"
import { environment } from '../../environments/environment'
import { MessageResponse } from '../interfaces/messages'
import { MessageType } from '../enum/constants.enum'
import { ApiResponse } from '../interfaces/api'
import { CreateFeedBackPayload, CreateFeedBackResponse } from '../interfaces/feedback'
import { SessionResponse } from '../interfaces/sessions';
import { transformMessageDtoToVm } from '../utils/messages.helper';
import { PostMessageService, WindowPostMessageAction } from './post-message.service';
import { MessagesApiService } from './messages.api.service';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthTokenService } from './auth-token.service';


@Injectable({
  providedIn: 'root',
})
export class ChatService {

  public isLoading: boolean = false;
  public isStream: boolean = false;
  public currentSessionId?: string;
  public flowId?: string;
  public schema?: string;
  public user_name?: string;
  public user_id?: string;
  public tweaks?: {};
  public greetingMessage?: string;
  public templateQuestions: { text: string }[] = [];
  public messagesSubject = new BehaviorSubject<IMessageList[]>([]);
  public messages$: Observable<IMessageList[]> = this.messagesSubject.asObservable();
  public messagesLoading = signal(false)
  public isOpened = signal(false)
  public scrollToBottom$ = new Subject<void>();
  /** Time without a load event after which an iframe download counts as taken */
  private readonly iframeSettleMs = 1500;
  /** How long a download iframe stays in the DOM before it's cleaned up */
  private readonly iframeCleanupMs = 60000;
  private readonly http = inject(HttpClient);
  private readonly messagesApiService = inject(MessagesApiService);
  private readonly postMessageService = inject(PostMessageService);
  private readonly authTokenService = inject(AuthTokenService);

  // Track current streaming state for cancellation
  private currentStreamReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private currentThreadId: string | null = null;
  private currentRunId: string | null = null;
  private currentStreamSubscription: Subscription | null = null;
  private currentPendingMessageId: string | null = null;
  public isStreamCancelled: boolean = false;

  constructor() {
    this.messagesSubject.next([]);
  }

  /** Browser's IANA time zone (e.g. "America/New_York"), sent to chat-api as x-user-timezone */
  private getUserTimezone(): string | undefined {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      return undefined;
    }
  }

  loadChatMessages(sessionId: string, options?: { showLoader?: boolean }) {
    const showLoader = options?.showLoader !== false;

    if (showLoader) {
      this.messagesSubject.next([]);
      this.isOpened.set(false);
      this.messagesLoading.set(true);
    }

    this.messagesApiService.getSessionMessagesById(sessionId)
      .pipe(map(res => res.data))
      .subscribe({
        next: (fullSession: SessionResponse) => {
          const sessionMessages = fullSession.messages.map((message: MessageResponse) => transformMessageDtoToVm(message, fullSession.id));

          this.messagesSubject.next(sessionMessages);

          this.postMessageService.sendMessage(fullSession.id, WindowPostMessageAction.SET_SESSION_ID);
          if (showLoader) {
            this.messagesLoading.set(false);
          }
        },
        error: () => {
          if (showLoader) {
            this.messagesLoading.set(false);
          }
        }
      });
  }


  sendMessage(text: string) {
    const body = {
      flow_id: this.flowId,
      sender_name: this.user_name,
      run: {
        input_value: text,
        input_type: "chat",
        output_type: "any",
        tweaks: this.tweaks,
        session_id: this.currentSessionId,
      }
    }

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const userTimezone = this.getUserTimezone();
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "x-schema": `${this.schema}`,
          ...(userTimezone && { "x-user-timezone": userTimezone }),
        });

        const url = `${environment.baseURL}/v1/message`;
        return this.http.post<ApiResponse<MessageResponse>>(url, body, { headers });
      })
    );
  }

  /**
   * Send message with LangGraph streaming support
   * @param text Message text
   * @param onChunk Callback for each streaming chunk
   * @param onComplete Callback when streaming completes
   * @param onError Callback for errors
   */
  sendMessageWithStreaming(
    text: string,
    onChunk: (chunk: any) => void,
    onComplete: (result: any) => void,
    onError: (error: any) => void
  ) {
    // Reset cancellation flag when starting a new stream
    this.isStreamCancelled = false;
    const url = `${environment.baseURL}/v1/message/stream`;

    const body = {
      flow_id: this.flowId,
      sender_name: this.user_name,
      run: {
        input_value: text,
        input_type: "chat",
        output_type: "any",
        tweaks: this.tweaks,
        session_id: this.currentSessionId,
      }
    };

    this.currentStreamSubscription = from(this.authTokenService.getAccessToken()).subscribe({
      next: (token) => {
        // Validate token before proceeding
        if (!token || token.trim() === '') {
          console.error('Auth token is null, undefined, or empty');
          onError(new Error('Authentication token is missing. Please log in again.'));
          return;
        }

        const userTimezone = this.getUserTimezone();
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "x-schema": `${this.schema}`,
          ...(userTimezone && { "x-user-timezone": userTimezone }),
        });

        // Create EventSource with POST body (using fetch for POST + SSE)
        this.streamWithPost(url, body, headers, onChunk, onComplete, onError);
      },
      error: (error) => {
        console.error('Failed to get auth token:', error);
        this.currentStreamSubscription = null;
        onError(error);
      }
    });
  }

  /**
   * Helper method to handle POST request with SSE streaming
   */
  private streamWithPost(
    url: string,
    body: any,
    headers: HttpHeaders,
    onChunk: (chunk: any) => void,
    onComplete: (result: any) => void,
    onError: (error: any) => void
  ) {
    // Set up timeout (5 minutes)
    const TIMEOUT_MS = 300000;
    let timeoutId: any;
    let lastActivityTime = Date.now();
    let streamCompleted = false;

    const resetTimeout = () => {
      lastActivityTime = Date.now();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (!streamCompleted && Date.now() - lastActivityTime >= TIMEOUT_MS) {
          console.error('Stream timeout - no activity for 60 seconds');
          streamCompleted = true;
          onError(new Error('Stream timeout - no response from server'));
        }
      }, TIMEOUT_MS);
    };

    resetTimeout();

    // Validate Authorization header before making request
    const authHeader = headers.get('Authorization');
    if (!authHeader || authHeader.trim() === '' || authHeader === 'Bearer null' || authHeader === 'Bearer undefined') {
      console.error('Invalid or missing Authorization header:', authHeader);
      streamCompleted = true;
      onError(new Error('Authentication token is missing or invalid. Please log in again.'));
      return;
    }

    const userTimezone = headers.get('x-user-timezone');

    // Use fetch with ReadableStream for SSE with POST
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
        'x-schema': headers.get('x-schema') || '',
        ...(userTimezone && { 'x-user-timezone': userTimezone }),
      },
      body: JSON.stringify(body),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No reader available from response');
        }

        // Store reader for cancellation
        this.currentStreamReader = reader;
        const decoder = new TextDecoder();
        let buffer = '';

        const readStream = (): Promise<void> => {
          return reader.read().then(({ done, value }) => {
            if (done) {
              clearTimeout(timeoutId);
              this.currentStreamReader = null;
              if (!streamCompleted) {
                streamCompleted = true;
                // Stream ended without completion event
                console.warn('Stream ended without completion event');
              }
              return;
            }

            resetTimeout(); // Reset timeout on each chunk

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));

                  // Track thread_id and run_id from chunks
                  if (data.thread_id) {
                    this.currentThreadId = data.thread_id;
                  }
                  if (data.run_id) {
                    this.currentRunId = data.run_id;
                  }
                  // Also check nested structures
                  if (data.result?.thread_id) {
                    this.currentThreadId = data.result.thread_id;
                  }
                  if (data.result?.run_id) {
                    this.currentRunId = data.result.run_id;
                  }

                  if (data.type === 'complete') {
                    clearTimeout(timeoutId);
                    streamCompleted = true;
                    this.currentStreamReader = null;
                    this.currentStreamSubscription = null;

                    // Don't process completion if stream was cancelled
                    if (this.isStreamCancelled) {
                      console.log('Stream completion ignored - stream was cancelled');
                      return;
                    }

                    // Extract thread_id and run_id from completion event
                    if (data.thread_id) {
                      this.currentThreadId = data.thread_id;
                    }
                    if (data.run_id) {
                      this.currentRunId = data.run_id;
                    }
                    // Also check result object
                    if (data.result?.thread_id) {
                      this.currentThreadId = data.result.thread_id;
                    }
                    if (data.result?.run_id) {
                      this.currentRunId = data.result.run_id;
                    }

                    // Track message ID if available (from completion event)
                    if (data.messageId) {
                      this.currentPendingMessageId = data.messageId;
                    }

                    // Pass the full data object (not just data.result) to include metadata and messageId
                    onComplete(data);

                    // Reset tracking after completion
                    this.currentThreadId = null;
                    this.currentRunId = null;
                    this.currentPendingMessageId = null;
                  } else if (data.type === 'error') {
                    clearTimeout(timeoutId);
                    streamCompleted = true;
                    this.currentStreamReader = null;
                    this.currentStreamSubscription = null;
                    onChunk(data); // Let the chunk handler deal with error display
                  } else if (data.type === 'thinking_step') {
                    // Live thinking updates: accumulate steps and store the latest step message for the last AI message.
                    const currentMessages = this.messagesSubject.getValue();
                    const step = data.step as { message?: string } | undefined;
                    const text = step?.message;
                    if (text) {
                      // Messages are stored newest-first.
                      // For LangGraph streaming, we add a single pending assistant message at index 0.
                      // Update that message so "thinking" renders on the current turn (not an older message).
                      const targetIndex = currentMessages.findIndex(
                        (m) =>
                          m &&
                          m.sender === MessageType.MACHINE &&
                          (m.isPending === true || m.id === '' || m.id === undefined),
                      );

                      const indexToUpdate =
                        targetIndex >= 0
                          ? targetIndex
                          : currentMessages.findIndex(
                              (m) => m && m.sender === MessageType.MACHINE,
                            );

                      if (indexToUpdate >= 0) {
                        const msg = currentMessages[indexToUpdate];
                        currentMessages[indexToUpdate] = {
                          ...msg,
                          thinkingStartedAtMs: msg.thinkingStartedAtMs ?? Date.now(),
                          currentThinkingStep: text,
                          thinkingSteps: [...(msg.thinkingSteps || []), text],
                        };
                        this.messagesSubject.next([...currentMessages]);
                      }
                    }
                    // Still forward to component-level handler if it wants to react
                    onChunk(data);
                  } else if (data.type === 'token') {
                    // First token means "thinking" phase is done.
                    // Compute duration from first thinking_step to first token.
                    const currentMessages = this.messagesSubject.getValue();

                    const targetIndex = currentMessages.findIndex(
                      (m) =>
                        m &&
                        m.sender === MessageType.MACHINE &&
                        (m.isPending === true || m.id === '' || m.id === undefined),
                    );
                    const indexToUpdate =
                      targetIndex >= 0
                        ? targetIndex
                        : currentMessages.findIndex(
                            (m) => m && m.sender === MessageType.MACHINE,
                          );

                    if (indexToUpdate >= 0) {
                      const msg = currentMessages[indexToUpdate];
                      const now = Date.now();
                      if (!msg.thinkingPhaseDone && msg.thinkingStartedAtMs != null) {
                        const durationSeconds = Math.max(
                          0,
                          Math.ceil((now - msg.thinkingStartedAtMs) / 1000),
                        );
                        currentMessages[indexToUpdate] = {
                          ...msg,
                          firstResponseChunkAtMs: msg.firstResponseChunkAtMs ?? now,
                          thoughtDurationSeconds:
                            msg.thoughtDurationSeconds ?? durationSeconds,
                          thinkingPhaseDone: true,
                          currentThinkingStep: `Thought for ${durationSeconds}s`,
                        };
                        this.messagesSubject.next([...currentMessages]);
                      }
                    }

                    // Forward token to component-level handler (it appends text)
                    onChunk(data);
                  } else {
                    // Don't process chunks if stream was cancelled
                    if (this.isStreamCancelled) {
                      return;
                    }

                    // Track message ID from chunks if available
                    if (data.messageId) {
                      this.currentPendingMessageId = data.messageId;
                    }
                    onChunk(data);
                  }
                } catch (e) {
                  console.warn('Failed to parse SSE data:', line);
                }
              }
            }

            return readStream();
          });
        };

        return readStream();
      })
      .catch(error => {
        clearTimeout(timeoutId);
        this.currentStreamReader = null;
        this.currentStreamSubscription = null;
        console.error('Streaming error:', error);
        if (!streamCompleted) {
          streamCompleted = true;
          onError(error);
        }
      });
  }

  /**
   * Cancel the current streaming request
   * @returns Promise that resolves with the message ID if it needs to be deleted, null otherwise
   */
  async cancelStream(): Promise<string | null> {
    // Mark stream as cancelled to prevent completion handler from updating UI
    this.isStreamCancelled = true;

    // Unsubscribe from the stream subscription if it exists
    if (this.currentStreamSubscription) {
      try {
        this.currentStreamSubscription.unsubscribe();
        this.currentStreamSubscription = null;
        console.log('Stream subscription unsubscribed');
      } catch (error) {
        console.error('Error unsubscribing from stream:', error);
      }
    }

    // Close the stream reader if it exists
    if (this.currentStreamReader) {
      try {
        await this.currentStreamReader.cancel();
        this.currentStreamReader = null;
        console.log('Stream reader cancelled');
      } catch (error) {
        console.error('Error cancelling stream reader:', error);
      }
    }

    // Call the cancel API endpoint if we have thread_id and run_id
    if (this.currentThreadId && this.currentRunId) {
      try {
        const token = await this.authTokenService.getAccessToken();
        if (!token || token.trim() === '') {
          console.error('Auth token is missing for cancel request');
        } else {
          const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-schema': this.schema || '',
          });

          const url = `${environment.baseURL}/v1/message/cancel`;
          const body = {
            thread_id: this.currentThreadId,
            run_id: this.currentRunId,
          };

          await this.http.post(url, body, { headers }).toPromise();
          console.log(`Cancelled run ${this.currentRunId} in thread ${this.currentThreadId}`);
        }
      } catch (error) {
        console.error('Error calling cancel API:', error);
        // Don't throw - we still want to close the stream even if API call fails
      }
    } else {
      console.warn('Cannot cancel: missing thread_id or run_id', {
        threadId: this.currentThreadId,
        runId: this.currentRunId,
      });
    }

    // Return the message ID if it exists (so it can be deleted)
    const messageId = this.currentPendingMessageId;

    // Reset tracking variables
    this.currentThreadId = null;
    this.currentRunId = null;
    this.currentPendingMessageId = null;

    return messageId;
  }

  /**
   * Delete a message from the database
   * @param messageId The message ID to delete
   * @returns Promise that resolves when deletion is complete
   */
  async deleteMessage(messageId: string): Promise<void> {
    if (!messageId) {
      console.warn('Cannot delete message: messageId is empty');
      return;
    }

    try {
      const token = await this.authTokenService.getAccessToken();
      if (!token || token.trim() === '') {
        console.error('Auth token is missing for delete request');
        return;
      }

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-schema': this.schema || '',
      });

      const url = `${environment.baseURL}/v1/message/${messageId}`;
      await this.http.delete(url, { headers }).toPromise();
      console.log(`Deleted message ${messageId} from database`);
    } catch (error) {
      console.error('Error deleting message from database:', error);
      // Don't throw - we still want to remove from UI even if DB deletion fails
    }
  }

  runStream(text?:string) {
    const params = {
      stream: true,
    }

    const body = {
      input_value: text,
      input_type: "chat",
      output_type: "chat",
      tweaks: this.tweaks,
      session_id: this.currentSessionId,
    }

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        });

        const url = `${environment.baseURL}/api/v1/run/${this.flowId}`;
        return this.http.post<any>(url, body, { headers, params });
      })
    );
  }

  getStream(stream_url: string, session_id: string) {
    const url = `${environment.baseURL}${stream_url}?session_id=${session_id}`;
    const eventSource = new EventSource(url);

    eventSource.onmessage = (event) => {
      if (event.data) {
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.chunk) {
            const lastMessage = this.messagesSubject.value[this.messagesSubject.value.length - 1];
            const currentMessages = this.messagesSubject.value;

            lastMessage.text += parsedData.chunk;

            currentMessages[currentMessages.length - 1] = {
              ...currentMessages[currentMessages.length - 1],
              text: lastMessage.text
            };
            this.messagesSubject.next([...currentMessages]);
          }
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;

        }
      }
    };

    eventSource.addEventListener('error', (e) => {
      this.isLoading = false;
        this.messagesSubject.value[this.messagesSubject.value.length - 1] = {
          id: '',
          sender: MessageType.MACHINE,
          text: "Sorry, something went wrong.",
          isError: true,
          session_id: this.currentSessionId,
          isPending: false
        };
        eventSource.close();
      });

    eventSource.addEventListener('close', (e) => {
      this.messagesSubject.value[this.messagesSubject.value.length - 1].isPending = false;
      eventSource.close();
    });


  }

  /** Shared bearer + x-schema headers for chat API calls. */
  withAuthHeaders(): Observable<HttpHeaders> {
    return defer(() => from(this.authTokenService.getAccessToken())).pipe(
      map(
        (token) =>
          new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'x-schema': this.schema || '',
          }),
      ),
    );
  }

  upsertFeedBack(body: CreateFeedBackPayload) {
    const apiUrl = `${environment.baseURL}/v1/feedback`;

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-schema': this.schema || '',
        });
        return this.http.post<ApiResponse<CreateFeedBackResponse>>(apiUrl, body, { headers });
      })
    );
  }

  deleteFeedback(feedbackId: string) {
    const apiUrl = `${environment.baseURL}/v1/feedback/${feedbackId}`;

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-schema': this.schema || '',
        });
        return this.http.delete<ApiResponse<{ success: boolean }>>(apiUrl, { headers });
      })
    );
  }


  /**
   * Track CSV download by sending PATCH request
   * @param messageId Message ID to track download for
   * @returns Observable with patch response
   */
  patchCsvDownload(messageId: string): Observable<ApiResponse<any>> {
    const url = `${environment.baseURL}/v1/message/${messageId}`;

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-schema': this.schema || '',
        });

        const body = {
          isCSVDownloaded: true
        };

        return this.http.patch<ApiResponse<any>>(url, body, { headers });
      })
    );
  }

  /**
   * Downloads a file without opening a new tab.
   * Our own API is fetched as a blob and saved through an object URL, so the
   * browser never navigates and the page doesn't flash. Signed links live on
   * another host that doesn't send CORS headers, so reading them as a blob is
   * impossible - those go straight to a hidden iframe, which downloads in place
   * just as quietly.
   * @returns Observable that completes once the file has been handed to the browser
   */
  downloadCsv(url: string): Observable<void> {
    if (!this.isReadableAsBlob(url)) {
      return this.downloadViaHiddenIframe(url);
    }

    return from(this.authTokenService.getAccessToken()).pipe(
      switchMap((token) => {
        const headers = token
          ? new HttpHeaders({ 'Authorization': `Bearer ${token}`, 'x-schema': this.schema || '' })
          : new HttpHeaders();

        return this.http.get(url, { headers, responseType: 'blob', observe: 'response' });
      }),
      map((response) => {
        const blob = response.body;
        if (!blob?.size) {
          throw new Error(`Download returned an empty body: ${url}`);
        }

        this.saveBlob(blob, this.resolveFileName(response.headers.get('Content-Disposition'), url));
      }),
      catchError((e) => {
        // Status 0 means the browser blocked the read (CORS) and never got to
        // the server - the iframe isn't subject to that, so it's worth a try.
        // Any status the server actually produced won't download in an iframe
        // either, so let it surface instead of hiding it behind a fallback.
        if (!(e instanceof HttpErrorResponse) || e.status !== 0) {
          return throwError(() => e);
        }

        console.warn('Blob download blocked, falling back to hidden iframe:', e);
        return this.downloadViaHiddenIframe(url);
      })
    );
  }

  /**
   * Only our own API and same-origin URLs are reachable with an XHR - anything
   * else is blocked by CORS before we ever see the response. Origins are
   * compared parsed, not by prefix, so the auth header can't leak to a host
   * that merely looks like ours ('//other.host', 'https://baseurl.evil.net').
   */
  private isReadableAsBlob(url: string): boolean {
    const origin = this.parseOrigin(url);
    return !!origin && (origin === this.parseOrigin(environment.baseURL) || origin === window.location.origin);
  }

  private parseOrigin(url: string): string | null {
    try {
      return new URL(url, window.location.href).origin;
    } catch {
      return null;
    }
  }

  private saveBlob(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    link.remove();

    // Revoke once the browser has picked up the blob
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  /**
   * Fallback for links we can't read as a blob. The iframe stays hidden, so the
   * download happens in place as long as the response is an attachment.
   * @returns Observable that completes once the browser has taken the download,
   *          or errors when the response turned out not to be a file
   */
  private downloadViaHiddenIframe(url: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      // A download aborts the iframe navigation, so `load` only fires when the
      // server answered with something renderable - an error page, not a file.
      iframe.onload = () => subscriber.error(new Error(`Download did not return a file: ${url}`));
      iframe.src = url;

      document.body.appendChild(iframe);

      // No load event within the grace period means the download was taken
      const settle = setTimeout(() => {
        subscriber.next();
        subscriber.complete();
      }, this.iframeSettleMs);

      // Removal is on its own timer: the element is gone long after the
      // subscription ends, so an in-flight transfer is never cut short
      setTimeout(() => iframe.remove(), this.iframeCleanupMs);

      return () => clearTimeout(settle);
    });
  }

  private resolveFileName(contentDisposition: string | null, url: string): string {
    // The RFC 5987 form first - it's the only one carrying percent-encoding
    const encoded = contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (encoded?.[1]) {
      return this.decodeSafely(encoded[1].trim().replace(/^"|"$/g, ''));
    }

    const plain = contentDisposition?.match(/filename="?([^";]+)"?/i);
    if (plain?.[1]) {
      return plain[1].trim();
    }

    const pathName = url.split('?')[0].split('/').pop();
    return pathName ? this.decodeSafely(pathName) : 'download.csv';
  }

  /** A literal '%' in a file name isn't a valid escape and would throw */
  private decodeSafely(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
